/* eslint-disable @atlassian/tangerine/import/no-parent-imports */

import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useQueryLoader } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box } from '@atlaskit/primitives/compiled';
import UFOCustomCohortData from '@atlaskit/react-ufo/custom-cohort-data';
import UFOLoadHold from '@atlaskit/react-ufo/load-hold';
import UFOSegment, { UFOGenAISegment } from '@atlaskit/react-ufo/segment';
import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';
import { type BackendExperiment } from '@atlassian/search-experiment/types';

import { initialState as bootstrapInitialState } from '../../common/constants/bootstrap';
import { PrimaryProductKeys } from '../../common/constants/primary-product-keys';
import { type ProductKeys } from '../../common/constants/product-keys';
import type { BootstrapVariables, SearchVariables } from '../../common/constants/relay/types';
import { ResultsStates } from '../../common/constants/results';
import type { SearchPreloadDataProvider } from '../../common/types/search-preload';
import { useAppContext } from '../../common/ui/app-context';
import { ErrorComponent } from '../../common/ui/error';
import type { Sections } from '../../common/ui/filters/app-filter-sidebar/app-filters/dynamic-sections';
import { ResetProductsWithTimeout } from '../../common/ui/filters/app-filter-sidebar/reset-products-with-timeout';
import { Landing } from '../../common/ui/landing';
import { Layout } from '../../common/ui/layout';
import { Body } from '../../common/ui/layout-body';
import { Header } from '../../common/ui/layout-header';
import { ResultsLoading } from '../../common/ui/results-loading';
import { searchResultsLoadUFOExperienceError } from '../../common/ui/ufo/experiences';
import { onDebugPanelShown } from '../../common/utils/events/debug-panel-shown';
import { onTrackAvailableProducts } from '../../common/utils/events/track-available-products';
import { getUnifiedSearchRightRailEnhancementsExpValue } from '../../common/utils/experiments';
import { getLiteModeCohortWithoutExposure } from '../../common/utils/experiments/get-lite-mode-cohort-without-exposure';
import { getApplicationMode } from '../../common/utils/get-application-mode';
import { getUser } from '../../common/utils/get-user';
import { isJiraProduct } from '../../common/utils/is-jira-product';
import { releaseReSearchTransitionHold } from '../../common/utils/re-search-transition';
import { getBootstrapVariables } from '../../common/utils/relay/get-bootstrap-variables';
import { getSearchVariables } from '../../common/utils/relay/get-search-variables';
import { disposeQueryUpdatedTime } from '../../common/utils/relay/query-updated-time';
import { buildSelectedSites } from '../../common/utils/sites';
import { useAnalytics } from '../../controllers/analytics';
import { AutoOpenRovoChatOnLanding } from '../../controllers/auto-open-rovo-chat';
import { ErrorBoundary } from '../../controllers/error-boundary';
import { useLogException } from '../../controllers/error-boundary/utils';
import { getSelectedFilters as getFilters } from '../../controllers/filters/selected/get-selected-filters';
import { SearchPageProactiveAiExperimentProvider } from '../../controllers/proactive-ai-experiment';
import {
	useAvailableProducts,
	useBootstrapState,
	useClearReferrer,
	useFeatures,
	useFiltersDiscoverability2Enabled,
	useQueryParams,
	useThirdPartyConfigs,
	useUnitMetadata,
} from '../../controllers/store/bootstrap';
import {
	initialState as searchInitialState,
	useSearchActions,
} from '../../controllers/store/search';

import Filters from './filters';
import { LiteHeaderSiteSelectFilter } from './filters/product-filters/site-select/lite-header-site-select-filter';
import { FullPageChat } from './full-page-chat';
import Input from './input';
import { LiteHeader } from './lite-header';
import { LiteHeaderFeedbackButton } from './lite-header-feedback-button';
import { AssetsAppMessageBanner } from './message-banners/assets-app-message-banner';
import { NewProductMessageBanner } from './message-banners/new-product-message-banner';
import { SearchPageQueryOrchestrator } from './query-plan';
import { Error } from './results/error';
import { RightPanel, RightPanelContainerResolver, RightPanelSkeleton } from './right-panel';
import RightPanelAsyncQuery, {
	type rightPanelAsyncQuery,
} from './right-panel/__generated__/rightPanelAsyncQuery.graphql';
import RightPanelQuery, {
	type rightPanelQuery,
} from './right-panel/__generated__/rightPanelQuery.graphql';
import { SearchPageHeader } from './search-page-header';
import SearchResultsAsyncQuery, {
	type searchResultsAsyncQuery,
} from './search-results/__generated__/searchResultsAsyncQuery.graphql';
import SearchResultsCqlQuery, {
	type searchResultsCqlQuery,
} from './search-results/__generated__/searchResultsCqlQuery.graphql';
import { FloatingRovoChatInput } from './search-results/floating-rovo-chat-input';
import { SearchResultsLiteTableSkeleton } from './search-results/search-result-list-lite-renderer/search-results-lite-table-skeleton';
import { SearchResultsAsync } from './search-results/search-results';
import { SearchResultsCQL } from './search-results/search-results-cql';
import SmartAnswers from './smart-answers';
import { getSearchConfig } from './utils/get-search-config';
import { getSearchPageMode, type SearchPageMode } from './utils/get-search-page-mode';

type VoidFunc = () => void;

export type SearchPageRelayProps = {
	aggAbsoluteUrl?: string;
	headerOffset?: number;
	debugPortalRef?: React.RefObject<HTMLDivElement>;
	preloadDataProvider?: SearchPreloadDataProvider;
	renderConnectorPromo?: () => React.ReactElement;
};

const autoCorrectStyles = cssMap({
	root: {
		'&:empty': {
			display: 'none',
		},
	},
});

/**
 * Memoized to prevent re-renders when SearchPageLayout re-renders due to bootstrap
 * context/store changes. SearchPageBody receives all reactive data from internal hooks
 * (useAppContext, useSearchActions, etc.) and local useState mirrors (set by
 * SearchPageQueryOrchestrator), not from parent props. Props are stable: aggAbsoluteUrl
 * is a string, debugPortalRef is a useRef. This keeps SearchPageBody off the critical
 * path for query start.
 */
const SearchPageBody = React.memo(
	({
		aggAbsoluteUrl,
		debugPortalRef,
		preloadDataProvider,
		renderConnectorPromo,
	}: SearchPageRelayProps) => {
		const [relaySearchResultsAsync, loadSearchResultsAsyncQuery, disposeSearchResultsAsyncQuery] =
			useQueryLoader<searchResultsAsyncQuery>(SearchResultsAsyncQuery);
		const [relayRightPanel, loadRightPanelQuery, disposeRightPanelQuery] =
			useQueryLoader<rightPanelQuery>(RightPanelQuery);
		const [relayRightPanelAsync, loadRightPanelAsyncQuery, disposeRightPanelAsyncQuery] =
			useQueryLoader<rightPanelAsyncQuery>(RightPanelAsyncQuery);
		const [
			searchResultsCQLQueryReference,
			loadSearchResultsCQLQuery,
			disposeSearchResultsCQLQuery,
		] = useQueryLoader<searchResultsCqlQuery>(SearchResultsCqlQuery);
		const [resultsState, setResultsState] = useState<ResultsStates>(ResultsStates.INITIAL);

		// Complete the re-search VC90 transition (if any) once the results reach a terminal
		// state (rendered / no-results / error). Releasing the imperative hold taken at the
		// search gesture lets the interaction settle at the VC90 point. No-op when no
		// re-search transition is in flight or the gate is off.
		useEffect(() => {
			if (
				resultsState === ResultsStates.RESULTS ||
				resultsState === ResultsStates.NO_RESULTS ||
				resultsState === ResultsStates.ERROR
			) {
				releaseReSearchTransitionHold();
			}
		}, [resultsState]);

		// Safety net: if the search page unmounts while a re-search transition is still in
		// flight (e.g. the user navigates away mid-search), release the imperative hold so the
		// interaction can settle instead of hanging until UFO's timeout, and so the shared
		// in-flight id is not left dangling. No-op when nothing is in flight.
		useEffect(() => releaseReSearchTransitionHold, []);
		const { queryParams, isAdminHubAIEnabled, isRovoLLMEnabled } = useAppContext();
		const { clearAllFilters, resetSort } = useSearchActions();
		const clearReferrer = useClearReferrer();
		const logError = useLogException();
		const { fireAnalyticsEvent } = useAnalytics();
		const [features] = useFeatures();
		const isSideBySideEnabled = features?.sideBySide?.enabled ?? false;
		const isLiteMode = features?.lite?.enabled ?? false;

		// Local state for bootstrap, search, and selected input filters to update when Relay query resolves
		const [bootstrap, setBootstrap] = useState<BootstrapVariables>(() =>
			getBootstrapVariables(bootstrapInitialState),
		);
		const [search, setSearch] = useState<SearchVariables>(() =>
			getSearchVariables(searchInitialState),
		);
		const [federatedSlackCount, setFederatedSlackCount] = useState<number | null>(null);
		const [federatedOutlookMailCount, setFederatedOutlookMailCount] = useState<number | null>(null);
		const [federatedGmailCount, setFederatedGmailCount] = useState<number | null>(null);
		const [hasMoreGmailResults, setHasMoreGmailResults] = useState<boolean>(false);
		const [hasMoreOutlookMailResults, setHasMoreOutlookMailResults] = useState<boolean>(false);
		const [hasRecordedAvailableProducts, setHasRecordedAvailableProducts] = useState(false);

		const [productsWithTimeout, setProductsWithTimeout] = useState<ProductKeys[]>();
		const [personalisedProductSections, setPersonalisedProductSections] =
			useState<Map<Sections, ProductKeys[]>>();

		// Sync thirdPartyConfigs from the bootstrap store into the local snapshot
		// so the right-panel sidebar reflects OAuth changes immediately.
		const [liveThirdPartyConfigs] = useThirdPartyConfigs();
		useEffect(() => {
			setBootstrap((prev) => {
				if (prev.thirdPartyConfigs === liveThirdPartyConfigs) {
					return prev;
				}
				return { ...prev, thirdPartyConfigs: liveThirdPartyConfigs };
			});
		}, [liveThirdPartyConfigs]);

		// Error boundary reset handling - creates a ref to store the reset callback
		// Calling resetErrorBoundary() will invoke the reset on the ErrorBoundary component without a remount
		const errorBoundaryResetCbRef = useRef<VoidFunc | undefined>(undefined);
		const setErrorBoundaryResetCb = useCallback((cb?: VoidFunc) => {
			errorBoundaryResetCbRef.current = cb; // undefined acts as a cleanup
		}, []);
		const resetErrorBoundary = useCallback(() => {
			errorBoundaryResetCbRef.current?.();
		}, []);

		const bodyRef = useRef<HTMLDivElement>(null);
		const mainContentRef = useRef<HTMLDivElement>(null);
		const [maxResultsToShow, setMaxResultsToShow] = useState<number | undefined>(undefined);
		const [mainContentWidth, setMainContentWidth] = useState<number | undefined>(undefined);

		const {
			cloudId,
			primaryProduct,
			primarySubProduct,
			searchSessionId,
			isRovoEnabled,
			allFilterExcludedProducts,
			experimentConfig,
			productEdition,
			productDataRegions,
			isJiraIssueIndexed,
			user: maybeUser,
			nouns,
			referrer,
		} = bootstrap;

		const { jsmIncidentFilterIds } = search;

		// Bootstrap params outside of local mirror state
		const [thirdPartyConfigs] = useThirdPartyConfigs();
		const availableProducts = useAvailableProducts();

		// Sites in scope for this search (the `?sites=` selection plus the current site)
		const unitMetadata = useUnitMetadata();
		const selectedSites = useMemo(
			() => buildSelectedSites(queryParams.sites, unitMetadata, cloudId),
			[queryParams.sites, unitMetadata, cloudId],
		);

		// Search parameters independent of search store
		const {
			cql,
			searchQuery,
			filters: filters_DO_NOT_USE_FOR_RENDERING,
			selectedProducts,
			selectedDatasource,
			pageMode,
		} = useMemo(
			() =>
				getSearchConfig({
					cloudId: cloudId || '',
					queryParams,
					thirdPartyConfigs,
				}),
			[cloudId, queryParams, thirdPartyConfigs],
		);

		const selectedProduct = selectedProducts[0];

		// QS-9805: In the lite Jira-table experience, when the user clicks a different
		// product filter `selectedProduct` updates immediately but the new preloaded
		// query ref doesn't arrive until a subsequent render. During that window the
		// existing query ref still resolves to the previous filter's data - rendering
		// it would briefly flash stale rows from another product. We capture the
		// selectedProduct in scope at the moment the queryRef identity changes (i.e.
		// when fresh data has arrived for a freshly-loaded query) and fall back to
		// the existing loading skeleton when the captured selection no longer matches
		// the live one. Scoped to lite mode only because the reported regression is
		// specific to the lite Jira-table consumer (Atlassian Home / Townsquare);
		// other consumers (Confluence FPS, Jira FPS) keep their existing behaviour to
		// avoid an unrequested skeleton flash on filter clicks.
		const activeSearchResultsQueryRef = relaySearchResultsAsync;
		const lastQueryRefForResultsRef = useRef(activeSearchResultsQueryRef);
		const lastSelectionForResultsRef = useRef(selectedProduct);
		if (lastQueryRefForResultsRef.current !== activeSearchResultsQueryRef) {
			lastQueryRefForResultsRef.current = activeSearchResultsQueryRef;
			lastSelectionForResultsRef.current = selectedProduct;
		}
		const resultsDataMatchesSelection = lastSelectionForResultsRef.current === selectedProduct;

		const appMode = getApplicationMode({ isRovoEnabled });

		const user = useMemo(() => getUser(maybeUser), [maybeUser]);
		/**
		 * QS-7760
		 *
		 * Our search page DOM looks like this:
		 * - 1. Input
		 * - 2. Autocorrect
		 * - 3. SAIN
		 * - 4. Results list
		 *
		 * 2 and 4 comes from the search query, which suspends.
		 * 3 needs to be rendered immediately to begin the SAIN query (which can show before results return), but also needs to render between 2 and 4 in DOM
		 * So we use a portal to insert 2 above 3
		 * Otherwise 3 will be within the common suspense parent of 2 and 4, and not render until the query is resolved
		 * We also dont want to shw 3 in the suspense fallback, as when suspense finishes a remount is triggered (which flashes the SAIN UI)
		 *
		 * We considered splitting 2 and 4 but would need to implement `usePreloadedQuery` independently (with all pagination, query vars etc) but decided to take this approach to reduce the risk surface/complexity of the queries changing
		 */
		const autoCorrectPortalRef = useRef<HTMLDivElement>(null);
		const autoCorrectPortal = <Box xcss={autoCorrectStyles.root} ref={autoCorrectPortalRef} />;

		// Ref to the SAIN portal which is used by the Navigational Card that's rendered in the smart answer component
		const smartAnswersPortalRef = useRef<HTMLDivElement>(null);

		// Ref to a full-width portal target in Body, used to render the Jira NL SAIN card above the two-column layout
		const fullWidthSainPortalRef = useRef<HTMLDivElement>(null);

		// Ref for backend experiment data used when fetching search results
		const backendExperimentRef = useRef<BackendExperiment>();

		// Tagging the SAIN mount point as a GenAI segment makes react-UFO (a) route SAIN's
		// load holds into the extended hold bucket so they no longer extend the search page's
		// interaction end time, and (b) classify DOM mutations under this subtree as
		// `mutation:gen-ai-*`, which are excluded from TTVC/VC90. This is the structural
		// replacement for the bespoke `search-page-smart-answers` exclusion (DOM-id containment
		// + `search-page-ignoring-smart-answers` extra payload) that react-UFO applies today.
		//
		// `search-page-sain` — rather than the inner `search-page-smart-answers` segment — is the
		// wrap point because it also encloses the SmartAnswersWrapper div and its portal targets,
		// so the GenAI fiber walk covers everything the DOM-id check covers today plus the
		// full-width Jira NL card portalled into Body.
		const SainSegment = fg('rovo_fps_mark_sain_as_gen_ai_segment') ? UFOGenAISegment : UFOSegment;

		// Ideally we can refactor filters so that we run this function only when the filters change.
		// This would allow us to have the object in state.
		const getSelectedFilters = useMemo(
			() => () =>
				getFilters({
					filters: filters_DO_NOT_USE_FOR_RENDERING,
					selectedProducts,
					availableProducts,
					allFilterExcludedProducts,
					thirdPartyConfigs,
					jsmIncidentFilterIds,
					currentUserId: user?.id,
				}),
			[
				filters_DO_NOT_USE_FOR_RENDERING,
				selectedProducts,
				availableProducts,
				allFilterExcludedProducts,
				thirdPartyConfigs,
				jsmIncidentFilterIds,
				user?.id,
			],
		);

		const resultsLoading =
			isLiteMode && isJiraProduct(selectedProduct) ? (
				<UFOLoadHold name="search-page-results-loading">
					<SearchResultsLiteTableSkeleton />
				</UFOLoadHold>
			) : (
				<UFOLoadHold name="search-page-results-loading">
					<ResultsLoading rowCount={10} />
				</UFOLoadHold>
			);

		const resultsConfigLoading = (
			<UFOLoadHold name="search-page-results-config-loading">
				<ResultsLoading rowCount={10} />
			</UFOLoadHold>
		);

		/**
		 * Our use of:
		 * 1. Suspense -> RightpanelContainer
		 * 2. rightPanelLoading -> Suspense<fallback={rightPanelLoading}>
		 * 3. AppFilterSidebarLoading
		 *
		 * Is so that we get a loading state on counts:
		 * - 3 is the skeleton state (during bootstrap, no connectors) - HOLD(seach-page-right-panel-config-loading)
		 * - 2 is the suspense state (during search, no counts, has connectors) - HOLD(seach-page-right-panel-counts-loading)
		 * - 1 is the end state (counts!)
		 *
		 * Note that FG(rovo-search-right-rail-personalisation) modifies this to:
		 * - 3 is the skeleton state (during bootstrap, no connectors) - HOLD(seach-page-right-panel-config-loading)
		 * - 2 is the suspense state (during counts query, no RR rendered) - HOLD(seach-page-right-panel-loading)
		 * - 1 is the end state (connectors + counts!)
		 *
		 * Problem is when we render 1, we throw away 2 which isn't great for performance.
		 *
		 * https://product-fabric.atlassian.net/browse/QS-7802 for improvement
		 */
		const rightPanelWithoutResultCounts = (
			<RightPanel
				cloudId={cloudId}
				searchQuery={searchQuery}
				primaryProduct={primaryProduct}
				experimentConfig={experimentConfig}
				selectedProducts={selectedProducts}
				user={user}
				appMode={appMode}
				selectedProduct={selectedProduct}
				selectedDatasource={selectedDatasource}
				availableNouns={nouns}
				availableProducts={availableProducts}
				thirdPartyConfigs={thirdPartyConfigs}
				personalisedProductSections={personalisedProductSections}
				productsWithTimeout={productsWithTimeout}
			/>
		);

		const rightPanelWithLoadingResultCounts = (
			<RightPanel
				cloudId={cloudId}
				searchQuery={searchQuery}
				primaryProduct={primaryProduct}
				experimentConfig={experimentConfig}
				selectedProducts={selectedProducts}
				user={user}
				appMode={appMode}
				selectedProduct={selectedProduct}
				selectedDatasource={selectedDatasource}
				availableNouns={nouns}
				availableProducts={availableProducts}
				thirdPartyConfigs={thirdPartyConfigs}
				personalisedProductSections={personalisedProductSections}
				productsWithTimeout={productsWithTimeout}
				resultCountsLoading={true}
			/>
		);

		// In this state the RR has connectors but no counts
		const rightPanelWithoutResultCountsLoading = (
			<UFOLoadHold name="search-page-right-panel-counts-loading">
				<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
				{rightPanelWithoutResultCounts}
			</UFOLoadHold>
		);

		// In this state, we're waiting for 1P+3P config to be available
		const rightPanelConfigLoadingSkeleton = (
			<UFOLoadHold name="search-page-right-panel-config-loading">
				<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
				<RightPanelSkeleton />
			</UFOLoadHold>
		);

		// In this state, we're waiting for the RR to be ready
		const rightPanelLoadingSkeleton = (
			<UFOLoadHold name="search-page-right-panel-loading">
				<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
				<RightPanelSkeleton />
			</UFOLoadHold>
		);

		// In this state, we're waiting for the RR to be ready, but apps are visible
		const rightPanelLoadingSkeletonWithAppsVisible = (
			<UFOLoadHold name="search-page-right-panel-counts-loading">
				<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
				{rightPanelWithLoadingResultCounts}
			</UFOLoadHold>
		);

		// Log a health metric for accessible products
		useEffect(() => {
			// When null or empty bootstrap isn't ready. Even failed bootstrap will have primaryProduct in the set
			if (availableProducts && availableProducts.length > 0 && !hasRecordedAvailableProducts) {
				fireAnalyticsEvent(
					onTrackAvailableProducts({
						availableProducts,
						searchSessionId,
					}),
				);
				setHasRecordedAvailableProducts(true);
			}
		}, [availableProducts, hasRecordedAvailableProducts, fireAnalyticsEvent, searchSessionId]);

		useEffect(() => {
			if (searchQuery.length === 0) {
				setProductsWithTimeout(undefined);
			}
		}, [searchQuery]);

		// Clean up on unmount
		useEffect(
			() => () => {
				// Dispose query updated time
				disposeQueryUpdatedTime();
			},
			[],
		);

		const bootstrapReady = availableProducts.length > 0;

		const hasAnyFilterSelected = useMemo(() => {
			return Boolean(selectedProduct) || getSelectedFilters().hasFiltersSelected;
		}, [selectedProduct, getSelectedFilters]);

		// Derive mode from the same local mirror state used for rendering.
		const mode: SearchPageMode = useMemo(
			() =>
				getSearchPageMode({
					bootstrapReady,
					availableProducts,
					selectedProduct,
					searchQuery,
					hasAnyFilterSelected,
					cql,
				}),
			[bootstrapReady, availableProducts, selectedProduct, searchQuery, hasAnyFilterSelected, cql],
		);

		const newProductMessageBanner = useMemo(
			() => (
				<NewProductMessageBanner
					searchQuery={searchQuery}
					selectedProduct={selectedProduct}
					selectedProducts={selectedProducts}
					primaryProduct={primaryProduct}
					cloudId={cloudId}
					experimentConfig={experimentConfig}
					user={user}
					applicationMode={appMode}
				/>
			),
			[
				searchQuery,
				selectedProduct,
				selectedProducts,
				primaryProduct,
				cloudId,
				experimentConfig,
				user,
				appMode,
			],
		);

		const content = (
			<>
				<SearchPageQueryOrchestrator
					preloadDataProvider={preloadDataProvider}
					loadSearchResultsAsyncQuery={loadSearchResultsAsyncQuery}
					disposeSearchResultsAsyncQuery={disposeSearchResultsAsyncQuery}
					loadRightPanelQuery={isLiteMode ? undefined : loadRightPanelQuery}
					disposeRightPanelQuery={isLiteMode ? undefined : disposeRightPanelQuery}
					loadRightPanelAsyncQuery={isLiteMode ? undefined : loadRightPanelAsyncQuery}
					disposeRightPanelAsyncQuery={isLiteMode ? undefined : disposeRightPanelAsyncQuery}
					loadSearchResultsCQLQuery={loadSearchResultsCQLQuery}
					disposeSearchResultsCQLQuery={disposeSearchResultsCQLQuery}
					setBootstrap={setBootstrap}
					setSearch={setSearch}
					setResultsState={setResultsState}
					resetErrorBoundary={resetErrorBoundary}
					bodyRef={bodyRef}
					setMaxResultsToShow={setMaxResultsToShow}
					backendExperimentRef={backendExperimentRef}
					isAdminHubAIEnabled={isAdminHubAIEnabled}
					isRovoLLMEnabled={isRovoLLMEnabled}
					logError={logError}
					cql={cql}
					searchQuery={searchQuery}
					filters={filters_DO_NOT_USE_FOR_RENDERING}
					selectedProducts={selectedProducts}
					queryParams={queryParams}
					isLiteMode={isLiteMode}
				/>
				{pageMode === 'chat' && fg('rovo_search_chat_convergence') ? (
					<UFOSegment name="search-page-chat">
						<FullPageChat
							cloudId={cloudId || ''}
							userId={user?.id}
							product={primaryProduct}
							seedQuery={searchQuery}
						/>
					</UFOSegment>
				) : (
					<Body
						bodyRef={bodyRef}
						mainContentRef={mainContentRef}
						setMainContentWidth={setMainContentWidth}
						selectedProduct={selectedProduct}
						sideBySide={isSideBySideEnabled}
						isLiteMode={isLiteMode}
						fullWidthSainPortalRef={fullWidthSainPortalRef}
						rightPanel={
							isLiteMode ? undefined : (
								<ErrorBoundary
									section="SearchPageRelayRightPanel"
									fallback={rightPanelWithoutResultCounts}
								>
									{bootstrapReady ? (
										// In RESULTS mode we suspend on the counts query, otherwise we render the prequery right rail.
										mode === 'RESULTS' ? (
											<Suspense
												fallback={
													fg('rovo-search-right-rail-personalisation')
														? getUnifiedSearchRightRailEnhancementsExpValue() !== 'single-select'
															? rightPanelLoadingSkeletonWithAppsVisible
															: rightPanelLoadingSkeleton
														: rightPanelWithoutResultCountsLoading
												}
											>
												{relayRightPanel || relayRightPanelAsync ? (
													<UFOSegment name="search-page-right-panel">
														<RightPanelContainerResolver
															cloudId={cloudId}
															searchQuery={searchQuery}
															primaryProduct={primaryProduct}
															experimentConfig={experimentConfig}
															selectedProducts={selectedProducts}
															user={user}
															appMode={appMode}
															rightPanelQueryRef={(relayRightPanel ?? relayRightPanelAsync)!}
															selectedProduct={selectedProduct}
															selectedDatasource={selectedDatasource}
															availableNouns={nouns}
															availableProducts={availableProducts}
															thirdPartyConfigs={thirdPartyConfigs}
															federatedSlackCount={federatedSlackCount}
															federatedOutlookMailCount={federatedOutlookMailCount}
															federatedGmailCount={federatedGmailCount}
															hasMoreGmailResults={hasMoreGmailResults}
															hasMoreOutlookMailResults={hasMoreOutlookMailResults}
															personalisedProductSections={personalisedProductSections}
															setPersonalisedProductSections={setPersonalisedProductSections}
															productsWithTimeout={productsWithTimeout}
														/>
													</UFOSegment>
												) : getUnifiedSearchRightRailEnhancementsExpValue() !== 'single-select' ? (
													rightPanelWithoutResultCountsLoading
												) : (
													rightPanelLoadingSkeleton
												)}
											</Suspense>
										) : (
											<>
												<ResetProductsWithTimeout setProductsWithTimeout={setProductsWithTimeout} />
												{rightPanelWithoutResultCounts}
											</>
										)
									) : (
										rightPanelConfigLoadingSkeleton
									)}
								</ErrorBoundary>
							)
						}
					>
						{autoCorrectPortal}
						{/* Owns the search_page_proactive_ai_multi_variant exposure: fires
					    once on non-empty-term landing (regardless of SAIN) and provides the
					    resolved cohort to all variant controllers below, which only read it.
					    Must wrap EVERY variant consumer — including the inline chat input
					    cohort rendered inside the results tree — otherwise context reads
					    return undefined and those cohorts never activate. */}
						<SearchPageProactiveAiExperimentProvider>
							<SainSegment name="search-page-sain">
								<SmartAnswers
									resultsState={resultsState}
									isLiteMode={isLiteMode}
									isRelayEnabled
									smartAnswersPortalRef={smartAnswersPortalRef}
									fullWidthSainPortalRef={fullWidthSainPortalRef}
									selectedSites={selectedSites}
								/>
							</SainSegment>
							{/* auto_open_chat_modal variant: auto-opens the Ask Rovo mini-modal.
						    Reads the shared cohort; never fires exposure itself. */}
							<AutoOpenRovoChatOnLanding />
							{/* floating_chat_input variant: only mount once results are shown
						    (not during bootstrap/landing/loading), so the floating chat box
						    never appears ahead of the results. */}
							{mode === 'RESULTS' && <FloatingRovoChatInput />}
							{primaryProduct === PrimaryProductKeys.Assets && !fg('assets_rovo_search_all') && (
								<AssetsAppMessageBanner />
							)}
							<ErrorBoundary
								section="SearchPageRelayResults"
								setErrorBoundaryResetCb={setErrorBoundaryResetCb}
								onException={() => {
									searchResultsLoadUFOExperienceError();
									setResultsState(ResultsStates.ERROR);
								}}
								// Note: Using renderWithErrorInfo instead of fallback to ensure Error component
								// always receives actual error details (not generic "Error" message).
								// This enables:
								// 1. Specific error messages in VitalStats taskFail events (e.g., "NetworkError: Failed to fetch")
								// 2. Better error tracking and debugging
								// 3. Single rendering path for consistency
								renderWithErrorInfo={({ error, errorInfo }) => {
									// Evaluate feature gate unconditionally to ensure exposure tracking
									// (required by @atlaskit/platform/no-preconditioning linter rule)
									const showErrorDetails = fg('result_error_info_in_non_prod');
									return (
										<>
											{/* ErrorComponent shows detailed error stack trace in non-prod when flag is on */}
											{showErrorDetails && process.env.NODE_ENV !== 'production' && (
												<ErrorComponent error={error} errorInfo={errorInfo || {}} />
											)}
											{/* Error component shown to user, receives actual error for VitalStats taskFail event */}
											<Error fetchResults={() => window.location.reload()} error={error} />
										</>
									);
								}}
							>
								{mode === 'BOOTSTRAP_LOADING' ? (
									resultsConfigLoading
								) : mode === 'LANDING' ? (
									<UFOLoadHold name="search-page-results-landing">
										<Landing
											applicationMode={appMode}
											isAdminHubAIEnabled={isAdminHubAIEnabled}
											isRovoLLMEnabled={isRovoLLMEnabled}
										/>
									</UFOLoadHold>
								) : (
									<Suspense fallback={resultsLoading}>
										{mode === 'RESULTS' && isLiteMode && !resultsDataMatchesSelection ? (
											resultsLoading
										) : mode === 'RESULTS' && relaySearchResultsAsync ? (
											<UFOSegment name="search-page-results">
												{newProductMessageBanner}
												<SearchResultsAsync
													searchResultsAsyncQuery={relaySearchResultsAsync}
													aggAbsoluteUrl={aggAbsoluteUrl}
													renderConnectorPromo={renderConnectorPromo}
													autoCorrectPortalRef={autoCorrectPortalRef}
													smartAnswersPortalRef={smartAnswersPortalRef}
													debugPortalRef={debugPortalRef}
													setResultsState={setResultsState}
													searchQuery={searchQuery}
													cloudId={cloudId}
													primaryProduct={primaryProduct}
													primarySubProduct={primarySubProduct}
													selectedProduct={selectedProduct}
													thirdPartyConfigs={thirdPartyConfigs}
													availableProducts={availableProducts}
													searchSessionId={searchSessionId}
													experimentConfig={experimentConfig}
													productEdition={productEdition}
													productDataRegions={productDataRegions}
													isJiraIssueIndexed={isJiraIssueIndexed}
													appMode={appMode}
													getSelectedFilters={getSelectedFilters}
													clearFilters={() => {
														clearAllFilters();
														resetSort();
													}}
													clearReferrer={clearReferrer}
													referrer={referrer}
													logError={logError}
													setFederatedSlackCount={setFederatedSlackCount}
													setFederatedOutlookMailCount={setFederatedOutlookMailCount}
													setFederatedGmailCount={setFederatedGmailCount}
													setHasMoreGmailResults={setHasMoreGmailResults}
													setHasMoreOutlookMailResults={setHasMoreOutlookMailResults}
													setProductsWithTimeout={setProductsWithTimeout}
													bodyRef={bodyRef}
													maxResultsToShow={maxResultsToShow}
													setMaxResultsToShow={setMaxResultsToShow}
													mainContentWidth={mainContentWidth}
													backendExperimentRef={backendExperimentRef}
													selectedSites={selectedSites}
												/>
											</UFOSegment>
										) : mode === 'CQL_RESULTS' && searchResultsCQLQueryReference ? (
											<UFOSegment name="search-page-results-cql">
												{newProductMessageBanner}
												<SearchResultsCQL
													availableProducts={availableProducts}
													backendExperimentRef={backendExperimentRef}
													bodyRef={bodyRef}
													clearReferrer={clearReferrer}
													cloudId={cloudId}
													experimentConfig={experimentConfig}
													getSelectedFilters={getSelectedFilters}
													isJiraIssueIndexed={isJiraIssueIndexed}
													maxResultsToShow={maxResultsToShow}
													productDataRegions={productDataRegions}
													productEdition={productEdition}
													referrer={referrer}
													searchQuery={searchQuery}
													searchResultsCQLQueryReference={searchResultsCQLQueryReference}
													searchSessionId={searchSessionId}
													selectedProduct={selectedProduct}
													primaryProduct={primaryProduct}
													primarySubProduct={primarySubProduct}
													appMode={appMode}
													mainContentWidth={mainContentWidth}
													setMaxResultsToShow={setMaxResultsToShow}
													setResultsState={setResultsState}
													thirdPartyConfigs={thirdPartyConfigs}
												/>
											</UFOSegment>
										) : (
											resultsLoading
										)}
									</Suspense>
								)}
							</ErrorBoundary>
						</SearchPageProactiveAiExperimentProvider>
					</Body>
				)}
			</>
		);

		return content;
	},
);

const DebugPanelPortal = ({
	debugPortalRef,
}: {
	debugPortalRef: React.RefObject<HTMLDivElement>;
}) => {
	const { fireAnalyticsEvent } = useAnalytics();

	useEffect(() => {
		fireAnalyticsEvent(onDebugPanelShown());
	}, [fireAnalyticsEvent]);

	return <Box ref={debugPortalRef} />;
};

export const SearchPageLayout = ({
	aggAbsoluteUrl,
	headerOffset,
	preloadDataProvider,
	renderConnectorPromo,
}: SearchPageRelayProps): React.JSX.Element => {
	const { isNav4Enabled } = useAppContext();
	const [features] = useFeatures();
	const [searchQueryParams] = useQueryParams();
	const { primaryProduct } = useBootstrapState(getBootstrapVariables);
	const debugPortalRef = useRef<HTMLDivElement>(null);
	const filtersDiscoverability2Enabled = useFiltersDiscoverability2Enabled();
	const unitMetadata = useUnitMetadata();
	const isSideBySideEnabled = features?.sideBySide?.enabled ?? false;
	const isLiteMode = features?.lite?.enabled ?? false;
	const isMultiSiteEnabled = (unitMetadata?.length ?? 0) > 1;
	const showSiteSelector =
		filtersDiscoverability2Enabled &&
		isLiteMode &&
		isMultiSiteEnabled &&
		serveFpsMultiSiteSearchExperiment({ primaryProduct });
	const liteModeCohort = getLiteModeCohortWithoutExposure();
	const hasOverrides = Boolean(searchQueryParams?.overrides);
	const enableRovoSearchOverrides = Boolean(
		// eslint-disable-next-line @atlaskit/platform/use-recommended-utils
		FeatureGates.getExperimentValue('enable-rovo_search_overrides', 'value', false),
	);
	const showDebugPanel = (isSideBySideEnabled || hasOverrides) && enableRovoSearchOverrides;

	const queryText =
		typeof searchQueryParams?.text === 'string' ? searchQueryParams.text : undefined;

	return (
		<>
			<UFOCustomCohortData dataKey="isLiteMode" value={isLiteMode} />
			<UFOCustomCohortData dataKey="liteModeCohort" value={liteModeCohort} />
			<SearchPageHeader primaryProduct={primaryProduct} query={queryText} />
			<Layout headerOffset={headerOffset}>
				{showDebugPanel && <DebugPanelPortal debugPortalRef={debugPortalRef} />}
				{isLiteMode ? (
					<LiteHeader
						input={isNav4Enabled ? null : <Input />}
						filters={<Filters />}
						feedback={<LiteHeaderFeedbackButton />}
						siteSelector={showSiteSelector ? <LiteHeaderSiteSelectFilter /> : undefined}
					/>
				) : (
					<Header input={isNav4Enabled ? null : <Input />} filters={<Filters />} />
				)}
				<SearchPageBody
					aggAbsoluteUrl={aggAbsoluteUrl}
					debugPortalRef={debugPortalRef}
					preloadDataProvider={preloadDataProvider}
					renderConnectorPromo={renderConnectorPromo}
				/>
			</Layout>
		</>
	);
};
